import { isDialablePhoneNumber } from './is-dialable-phone-number';
import { isInternalExtension } from './is-internal-extension';

/**
 * Prefix the FreeSWITCH outbound dialplan matches on (`DialplanConfigurationHandler`'s
 * `^(?:00|ob[-_])(\d+)$` regex) to route a call to the outbound gateway.
 */
const OUTBOUND_DIAL_PREFIX = '00';

/**
 * Frontend mirror of that dialplan's own extension regex (`^(?:00|ob[-_])(\d+)$`), built from
 * {@link OUTBOUND_DIAL_PREFIX} so the value {@link buildOutboundDialDestination} writes and the
 * classification {@link isOutboundGatewayDestination} reads cannot drift apart.
 */
const OUTBOUND_GATEWAY_DESTINATION_PATTERN = new RegExp(
  `^(?:${OUTBOUND_DIAL_PREFIX}|ob[-_])\\d+$`,
);

/**
 * Default country code assumed for a bare 10-digit national number, mirroring the backend's
 * `PhoneNumberSettingsProvider.DefaultCountryCodeValue` ("90", Turkey) — the value
 * `PhoneNumberConverter.TryNormalizeToE164` falls back to when a number carries neither a trunk
 * `0` nor a country code. The frontend has no access to the tenant-configurable setting this
 * mirrors, so the host default is hardcoded rather than left unresolved.
 */
const DEFAULT_COUNTRY_CODE = '90';

/**
 * A bare national significant number's digit length under the assumed numbering plan (Turkey):
 * neither a trunk `0` nor a country code, e.g. `5551234567`. Distinguishes this shape from an
 * 11-digit trunk-`0`-prefixed number (`05551234567`, already dialable via a plain `00` prefix)
 * and from a 12-digit number that already carries the country code.
 */
const BARE_NATIONAL_NUMBER_LENGTH = 10;

/**
 * Rewrites a dial destination into the form the backend FreeSWITCH outbound dialplan expects,
 * leaving internal (agent-to-agent) extensions untouched.
 *
 * Applied centrally in {@link CallFacadeService.makeCall} so every outbound dial entry point
 * (campaign "Call Now", manual dialpad number, queue-override dial) gets the same treatment
 * without duplicating the prefix logic at each call site.
 *
 * - Anything that is not a dialable phone number (see {@link isDialablePhoneNumber}) is returned
 *   unchanged. This covers non-phone dial destinations such as the FreeSWITCH FIFO dial string
 *   used by queue-override dial (`queue_{domainName}_{queueName}`, built by the backend's
 *   `FifoExtensions.BuildDialString()`) — the internal/external distinction below is meaningless
 *   for a value that isn't a phone number to begin with, and prefixing it would produce a string
 *   the dialplan can't route.
 * - Once a value is confirmed dialable, cosmetic separators (spaces, dashes, dots, parentheses)
 *   are stripped exactly once. `isDialablePhoneNumber` explicitly accepts these separators
 *   (e.g. `"+90 532 123 45 67"`, `"(212) 555-0123"`), but the FreeSWITCH dialplan regex
 *   (`^(?:00|ob[-_])(\d+)$`) and the internal-extension regex only match digits — leaving
 *   separators in would produce a string neither can route. The normalized value is what feeds
 *   every check and the final return below, so the internal/external classification and the
 *   value handed to SIP.js always agree.
 * - Internal (3-5 digit) extensions are returned in normalized form — they are never routed
 *   through the outbound gateway and must not receive the `00` prefix (see
 *   {@link isInternalExtension}, the frontend mirror of the backend's `Local_Extension` dialplan).
 * - A normalized destination already starting with `00` is returned unchanged (avoids
 *   double-prefixing).
 * - A leading `+` (E.164, the form the backend/CRM contract returns phone numbers in, e.g.
 *   `PhoneNumberConverter`) is swapped for `00` rather than prepended alongside it, because the
 *   dialplan regex only matches digits after the prefix.
 * - A bare {@link BARE_NATIONAL_NUMBER_LENGTH}-digit national number — neither a trunk `0` nor a
 *   country code, e.g. `5551234567` — gets {@link DEFAULT_COUNTRY_CODE} inserted before the `00`
 *   prefix (`005551234567` would otherwise tell the dialplan the "country code" is a single `5`,
 *   which cannot route). This mirrors `PhoneNumberConverter.TryNormalizeToE164`'s own handling of
 *   this exact digit count on the backend.
 * - Any other (bare, already trunk-`0`- or country-code-prefixed) number gets `00` prepended
 *   as-is.
 *
 * @param destination raw dial value about to be handed to SIP.js.
 * @returns the destination string that should actually be dialed.
 */
export function buildOutboundDialDestination(destination: string): string {
  if (!isDialablePhoneNumber(destination)) {
    return destination;
  }
  const normalized = destination.trim().replace(/[\s\-().]/g, '');
  if (isInternalExtension(normalized)) {
    return normalized;
  }
  if (normalized.startsWith(OUTBOUND_DIAL_PREFIX)) {
    return normalized;
  }
  if (normalized.startsWith('+')) {
    return `${OUTBOUND_DIAL_PREFIX}${normalized.slice(1)}`;
  }
  const withCountryCode =
    normalized.length === BARE_NATIONAL_NUMBER_LENGTH && !normalized.startsWith('0')
      ? `${DEFAULT_COUNTRY_CODE}${normalized}`
      : normalized;
  return `${OUTBOUND_DIAL_PREFIX}${withCountryCode}`;
}

/**
 * Whether the given dial destination is one the FreeSWITCH outbound dialplan routes to the
 * carrier gateway — i.e. an external call leaving the switch.
 *
 * This is the *read* side of {@link buildOutboundDialDestination}: it classifies the string that
 * function produced (the exact value handed to SIP.js) rather than re-deriving the decision from
 * the raw dialer input, so there is a single authority for "did this call get the gateway
 * prefix?". Re-implementing the internal/external classification at a second site is what let the
 * frontend and the backend disagree before.
 *
 * It matters beyond routing: the outbound branch of the backend's FreeSWITCH call-tracking
 * pipeline (`FreeSwitchCallTrackingDomainService.HandleOutboundCallAsync`) only runs for a channel
 * the dialplan flagged with `cx_outbound_call`, and that branch is what stamps the agent A-leg's
 * SIP Call-ID onto the conversation segment. A destination this predicate rejects — an internal
 * extension, or a FIFO queue dial string — never reaches that handler, so no A-leg Call-ID is ever
 * recorded for it and looking one up would poll forever.
 *
 * @param destination dial destination as handed to SIP.js (post-{@link buildOutboundDialDestination}).
 * @returns whether the destination carries the outbound gateway prefix.
 */
export function isOutboundGatewayDestination(destination: string): boolean {
  return OUTBOUND_GATEWAY_DESTINATION_PATTERN.test(destination.trim());
}
